import {
  OrderStatus,
  PaymentType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { usePaymentTerminalContext } from "contexts/PaymentTerminalContext";
import groupBy from "lodash/groupBy";
import { useOrderContext } from "providers/OrderProvider/hooks";
import { useCallback, useMemo } from "react";
import {
  NavigationProp,
  useFocusEffect,
  useNavigation,
} from "@react-navigation/native";
import { EventNavigationParamList } from "navigation/EventNavigation/types";
import { SCREENS } from "navigation/consts";
import useEvictQueries from "hooks/useEvictQueries";
import {
  GET_EVENT_OCCURRENCES,
  GET_EVENT_OCCURRENCE,
} from "@ticketingplatform/api/dist/event";
import { TICKET_LIST_QUERY } from "@ticketingplatform/api/dist/ticket";
import useIntlApolloError from "hooks/useIntlApolloError";

import { Checkout, CheckoutLine } from "./types";
import { checkoutErrorMessages } from "./consts";

export const useCheckout = (): Checkout => {
  const evictQueries = useEvictQueries();
  const { paymentTerminal } = usePaymentTerminalContext();
  const {
    order,
    loading,
    error: apolloError,
    setOrder,
    removeOrder,
  } = useOrderContext();
  const { navigate } =
    useNavigation<NavigationProp<EventNavigationParamList>>();

  const payWithCash = useCallback(async () => {
    await setOrder({
      paymentType: PaymentType.CASH,
      paymentTerminalId: null,
    });

    navigate(SCREENS.Payment);
  }, [navigate, setOrder]);

  const payWithTerminal = useCallback(async () => {
    await setOrder({
      paymentType: PaymentType.STRIPE_TERMINAL,
      paymentTerminalId: paymentTerminal?.id,
    });

    navigate(SCREENS.Payment);
  }, [setOrder, navigate, paymentTerminal?.id]);

  const payWithPos = useCallback(async () => {
    await setOrder({
      paymentType: PaymentType.POS,
      paymentTerminalId: null,
    });

    navigate(SCREENS.Payment);
  }, [navigate, setOrder]);

  const checkoutWithoutPayment = useCallback(async () => {
    const newOrder = await setOrder({
      paymentType: null,
      paymentTerminalId: null,
      status: OrderStatus.CHECKOUT,
    });

    if (!newOrder) {
      return;
    }

    if (newOrder.status === OrderStatus.COMPLETED) {
      evictQueries([
        GET_EVENT_OCCURRENCES,
        GET_EVENT_OCCURRENCE,
        TICKET_LIST_QUERY,
      ]);
    }

    navigate(SCREENS.OrderDetails, {
      orderId: newOrder.id,
      pool: newOrder.status !== OrderStatus.COMPLETED,
    });

    removeOrder();
  }, [evictQueries, navigate, removeOrder, setOrder]);

  const lines = useMemo<CheckoutLine[]>(
    () =>
      Object.entries(groupBy(order?.tickets?.nodes, "name")).map(
        ([name, { length }]) => ({
          name,
          quantity: length,
        })
      ),
    [order?.tickets?.nodes]
  );

  useFocusEffect(
    useCallback(() => {
      if (!order && !loading) {
        navigate(SCREENS.Cart);
      }
    }, [loading, navigate, order])
  );

  const error = useIntlApolloError(checkoutErrorMessages, apolloError);

  return {
    lines,
    loading,
    total: order?.totalAmount || 0,
    error: error?.message,
    payWithCash,
    payWithTerminal,
    payWithPos,
    checkoutWithoutPayment,
  };
};
